import { useCallback, useEffect, useMemo, useState } from 'react';

const PER_PAGE = 15;

const normalizeBoolean = (value) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'si' || normalized === 'sí';
  }
  return Boolean(value);
};

const normalizePayload = (values = {}) => ({
  nombre: (values.nombre || '').trim(),
  id_boh: values.id_boh ? Number(values.id_boh) : null,
  rbi_id: (values.rbi_id || '').trim(),
  fecha_apertura: values.fecha_apertura || null,
  rational: Number.isFinite(Number(values.rational)) ? Number(values.rational) : 0,
  warmer: Number.isFinite(Number(values.warmer)) ? Number(values.warmer) : 0,
  calculo_automatico: normalizeBoolean(values.calculo_automatico)
});

export const useAdminRestaurants = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchList = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: PER_PAGE.toString(),
        search: search || ''
      });
      const response = await fetch(`/admin/api/restaurants?${params.toString()}`, { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener los restaurantes');
      }
      if (!signal?.aborted) {
        setItems(data.restaurants || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar restaurantes');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [page, search]);

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [fetchList, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const setSearch = useCallback((value) => {
    setPage(1);
    setSearchValue(value);
  }, []);

  const saveRestaurant = useCallback(async (values) => {
    const payload = normalizePayload(values);
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/restaurants/${values.id}` : '/admin/api/restaurants';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar el restaurante');
    }
    return data;
  }, []);

  const deleteRestaurant = useCallback(async (id) => {
    const response = await fetch(`/admin/api/restaurants/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo eliminar el restaurante');
    }
    return data;
  }, []);

  const loadSyncPreview = useCallback(async () => {
    const response = await fetch('/admin/api/restaurants/sync-preview');
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo obtener la sincronización');
    }
    return data;
  }, []);

  const syncSelected = useCallback(async (selection) => {
    const response = await fetch('/admin/api/restaurants/sync-boh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_sucursales: selection })
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo completar la sincronización');
    }
    return data;
  }, []);

  const syncDivisionAreas = useCallback(async () => {
    const response = await fetch('/admin/api/restaurants/sync-boh-areas', { method: 'POST' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo sincronizar áreas y divisiones');
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    total,
    page,
    perPage: PER_PAGE,
    loading,
    error,
    search,
    setPage,
    setSearch,
    refresh,
    saveRestaurant,
    deleteRestaurant,
    loadSyncPreview,
    syncSelected,
    syncDivisionAreas
  }), [
    items,
    total,
    page,
    loading,
    error,
    search,
    setSearch,
    refresh,
    saveRestaurant,
    deleteRestaurant,
    loadSyncPreview,
    syncSelected,
    syncDivisionAreas
  ]);
};
